import { Box, Skeleton, Stack, Typography } from '@mui/material'
import { useCallback, useMemo, useState } from 'react'
import { Button, Icons, InformationModal, SkeletonText } from 'utoniq-ui'

import {
  ItemType,
  PaymentCreateDetailFragment,
  PaymentMethod,
  PreSaleLotteryApplicantDetailFragment,
  PreSaleLotteryApplicantQuery,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { LotteryChoiceItem, LotteryChoicePrice } from '@/components/organisms'
import { config } from '@/config'
import { StorageImage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { generateCreditCardLogoPath } from '@/core/creditCard'
import { DateTime } from '@/core/entities'
import { encodeSkuId } from '@/core/sku'

export * from './fragments'

export type State = {
  loading: boolean
  submittingCancel: boolean
  preSaleLotteryApplicant:
    | PreSaleLotteryApplicantQuery['preSaleLotteryApplicant']
    | undefined
  paymentSource: PaymentCreateDetailFragment | null
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onCancelLaffleApplication: () => Promise<void>
}

export const LotteryApplicationDetailTemplate: Template<State, Action> = ({
  state,
  onCancelLaffleApplication,
}) => {
  const { t } = useTranslation('lotteryApplicationDetail')
  const { routerNames, routerPages } = useRouter()
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const handleOpenCancelModal = () => setModalOpen(true)

  /** コンビニ支払いかどうか */
  const isKonbini = useMemo(
    () =>
      state.preSaleLotteryApplicant?.paymentMethod === PaymentMethod.Konbini,
    [state.preSaleLotteryApplicant?.paymentMethod]
  )

  /** 当選したアイテム */
  const winChoicedItem = useMemo(
    () =>
      state.preSaleLotteryApplicant?.winChoiceNumber
        ? state.preSaleLotteryApplicant?.choiceItemDetails?.[
            state.preSaleLotteryApplicant?.winChoiceNumber - 1
          ]
        : null,
    [
      state.preSaleLotteryApplicant?.winChoiceNumber,
      state.preSaleLotteryApplicant?.choiceItemDetails,
    ]
  )

  /** コンビニ未払いの場合の決済導線 */
  const renderBottomItem = useCallback(() => {
    if (
      !(
        isKonbini &&
        state.preSaleLotteryApplicant?.status === '20_waiting_payment'
      )
    ) {
      return null
    }
    return (
      <FixedBottomCell
        expiredAt={
          state.preSaleLotteryApplicant?.cartTransactionOrder
            ?.pendingPurchaseItem?.formattedExpiredAt ?? ''
        }
        voucherUrl={
          state.preSaleLotteryApplicant?.cartTransactionOrder
            ?.pendingPurchaseItem?.voucherUrl ?? ''
        }
      />
    )
  }, [state.preSaleLotteryApplicant?.cartTransactionOrder, isKonbini])

  return (
    <BaseLayout
      header={{
        title: t('title'),
        back: true,
        defaultBackPage: routerPages.LotteryApplications,
      }}
      customNavigation={renderBottomItem()}
    >
      <Head title={routerNames.LotteryApplicationDetail} />
      {state.loading ? (
        <Box>
          <LoadingCell />
        </Box>
      ) : (
        <>
          <StatusCell
            isKonbini={isKonbini}
            status={state.preSaleLotteryApplicant?.status}
            isPaymentError={!!state.preSaleLotteryApplicant?.isPaymentError}
            paymentDeadline={
              state.preSaleLotteryApplicant?.preSale?.lottery
                ?.purchasableEndedAt
            }
            preSaleId={state.preSaleLotteryApplicant?.preSale?.id}
            winChoiceNumber={state.preSaleLotteryApplicant?.winChoiceNumber}
            winChoicedItem={winChoicedItem}
          />
          <DetailCell
            preSaleLotteryApplicant={state.preSaleLotteryApplicant}
            paymentSource={state.paymentSource}
            eventTitle={state.preSaleLotteryApplicant?.preSale?.event?.title}
            onCancel={handleOpenCancelModal}
            preSaleId={state.preSaleLotteryApplicant?.preSale?.id}
            winChoicedItem={winChoicedItem}
            isKonbini={isKonbini}
          />
        </>
      )}
      <InformationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={t('cancelModalTitle')}
        content={t('cancelModalDesc')}
        buttons={
          <Button
            size="large"
            sx={{ width: 212 }}
            onClick={() => {
              onCancelLaffleApplication().then(() => setModalOpen(false))
            }}
            loading={state.submittingCancel}
            disabled={
              state.preSaleLotteryApplicant?.status !== '30_waiting_lottery'
            }
          >
            {t('cancelModalButton')}
          </Button>
        }
        cancelText={t('cancelModalClose')}
      />
    </BaseLayout>
  )
}

const LoadingCell = () => (
  <CellFrame sx={{ pt: 7, px: 5, pb: 5 }}>
    <Stack alignItems="center">
      <SkeletonText variant="h6" width="60%" />
    </Stack>
    <CellFrame sx={{ pb: 6 }}>
      <Stack sx={{ pt: 9 }}>
        <Stack sx={{ bgcolor: 'grey.50' }}>
          <Skeleton
            variant="rectangular"
            height={20}
            width="100%"
            sx={{ bgcolor: 'grey.100', my: '6px' }}
          />
        </Stack>
        <SkeletonText variant="body2" numberOfLine={2} lastWidth="25%" />
      </Stack>
      <Stack sx={{ pt: 6 }}>
        <SkeletonText variant="body2" numberOfLine={5} />
      </Stack>
      <Stack sx={{ pt: 9 }}>
        <Stack sx={{ bgcolor: 'grey.50' }}>
          <Skeleton
            variant="rectangular"
            height={20}
            width="100%"
            sx={{ bgcolor: 'grey.100', my: '6px' }}
          />
        </Stack>
        <SkeletonText variant="body2" numberOfLine={6} />
      </Stack>
      <Stack sx={{ pt: 9 }}>
        <Stack sx={{ bgcolor: 'grey.50' }}>
          <Skeleton
            variant="rectangular"
            height={20}
            width="100%"
            sx={{ bgcolor: 'grey.100', my: '6px' }}
          />
        </Stack>
        <SkeletonText variant="body2" numberOfLine={6} />
      </Stack>
    </CellFrame>
  </CellFrame>
)

type StatusCellProps = {
  status: string | undefined
  paymentDeadline: string
  isPaymentError: boolean
  isKonbini: boolean
  preSaleId?: string
  winChoiceNumber: PreSaleLotteryApplicantDetailFragment['winChoiceNumber']
  winChoicedItem:
    | PreSaleLotteryApplicantDetailFragment['choiceItemDetails'][number]
    | null
}
const StatusCell: Cell<StatusCellProps> = ({
  status,
  isKonbini,
  paymentDeadline,
  isPaymentError,
  preSaleId,
  winChoiceNumber,
  winChoicedItem,
}) => {
  const { t } = useTranslation('lotteryApplicationDetail')
  const { router, generatePath, routerPages } = useRouter()

  /**
   * 当選時、ボタンを押した時の処理
   * 支払い済み→アイテム詳細へ
   * 支払い待ち
   *   →クレカ(失敗)なら決済ページへ
   *   →コンビニなら非表示
   */
  const handleClickWinButton = useCallback(() => {
    if (!winChoicedItem) return
    if (status === '20_waiting_payment' && isKonbini) return

    // クレカ失敗なら決済ページへ
    if (status === '20_waiting_payment' && !isKonbini) {
      return router.push(
        generatePath(routerPages.LotteryApplicationDetailPaymentConfirmation, {
          preSaleId: preSaleId ?? '#',
        })
      )
    }

    // 支払い済みの場合、アイテム詳細へ
    const { itemId, skuId } = winChoicedItem
    return router.push({
      pathname: generatePath(routerPages.OwnedItemDetail, {
        itemId,
      }),
      search: skuId ? `?skuId=${encodeSkuId(skuId ?? '')}` : '',
    })
  }, [status, winChoiceNumber, winChoicedItem])

  return (
    <CellFrame sx={{ py: 7, px: 5 }}>
      <Stack
        direction="row"
        justifyContent="center"
        spacing={3}
        alignItems="center"
      >
        {/* 無効・キャンセル・クレカ失敗時はエラーを表示 */}
        {status === '50_invalid' ||
        (status === '20_waiting_payment' && !isKonbini) ||
        status === '60_canceled' ? (
          <Icons.Error
            sx={{
              color: status === '60_canceled' ? 'primary.main' : 'error.main',
            }}
          />
        ) : (
          <Icons.Check sx={{ color: 'primary.main' }} />
        )}
        <Typography variant="h6">
          {isKonbini ? t(`konbini_${status}_title`) : t(`${status}_title`)}
        </Typography>
      </Stack>

      <Stack sx={{ pt: 2 }}>
        <Typography variant="subtitle1" color="text.secondary" align="center">
          {isKonbini ? t(`konbini_${status}_desc`) : t(`${status}_desc`)}
        </Typography>
      </Stack>

      {/* クレカ失敗時、またはコンビニ未払いのとき、支払い期限を表示 */}
      {status === '20_waiting_payment' && (isPaymentError || isKonbini) && (
        <Box
          sx={{
            borderColor: 'error.main',
            borderWidth: 1,
            borderStyle: 'solid',
            py: 2,
            mt: 6,
          }}
        >
          <Typography variant="subtitle1" color="error.main" align="center">
            {t('paymentDeadline', { date: DateTime.format(paymentDeadline) })}
          </Typography>
        </Box>
      )}

      {/* 当選ステータスの場合、アイテム詳細や支払いページに行くためのボタンを表示 */}
      {((isKonbini && status === '11_win_paid') ||
        (!isKonbini &&
          ['11_win_paid', '20_waiting_payment'].includes(`${status}`))) && (
        <Stack sx={{ pt: 8 }}>
          <Stack direction="row" sx={{ justifyContent: 'center' }}>
            <Button
              size="large"
              sx={{ py: 2, px: 4, width: 244 }}
              onClick={handleClickWinButton}
            >
              {t(`${status}_button`)}
            </Button>
          </Stack>
        </Stack>
      )}
    </CellFrame>
  )
}

type DetailCellProps = {
  preSaleLotteryApplicant: State['preSaleLotteryApplicant']
  paymentSource: PaymentCreateDetailFragment | null
  onCancel: () => void
  winChoicedItem:
    | PreSaleLotteryApplicantDetailFragment['choiceItemDetails'][number]
    | null
  eventTitle?: string
  preSaleId?: string
  isKonbini: boolean
}

const DetailCell: Cell<DetailCellProps> = ({
  preSaleLotteryApplicant,
  paymentSource,
  onCancel,
  winChoicedItem,
  preSaleId,
  eventTitle,
  isKonbini,
}) => {
  const { t } = useTranslation('lotteryApplicationDetail')
  const { router, generatePath, routerPages } = useRouter()

  /**
   * ボタンを表示する条件
   * - 支払い済み、もしくは抽選待ちの場合に表示する
   * - クレカ支払いの場合は、エラー時の支払い待ちの状態に表示する
   * - マチゼロの場合は、キャンセルできない
   */
  const isShowButton = useMemo(() => {
    if (!preSaleLotteryApplicant) return false
    if (isKonbini) {
      return (
        config.applicationKey === 'machizero'
          ? ['11_win_paid']
          : ['11_win_paid', '30_waiting_lottery']
      ).includes(preSaleLotteryApplicant.status)
    }

    return (
      config.applicationKey === 'machizero'
        ? ['11_win_paid', '20_waiting_payment']
        : ['11_win_paid', '20_waiting_payment', '30_waiting_lottery']
    ).includes(preSaleLotteryApplicant.status)
  }, [preSaleLotteryApplicant, isKonbini])

  /** ボタンが押せない条件 */
  const isButtonDisabled = useMemo(() => {
    return (
      /** 抽選待ちかつ、応募締切を過ぎた場合 */
      preSaleLotteryApplicant?.status === '30_waiting_lottery' &&
      new Date() >
        new Date(preSaleLotteryApplicant?.preSale.lottery?.applicationEndedAt)
    )
  }, [
    preSaleLotteryApplicant?.status,
    preSaleLotteryApplicant?.preSale.lottery?.applicationEndedAt,
  ])

  const isShowUsageTicketNote = useMemo(
    () =>
      preSaleLotteryApplicant?.preSale?.items?.some(
        item => item.type === ItemType.Ticket
      ),
    [preSaleLotteryApplicant?.preSale?.items]
  )

  if (!preSaleLotteryApplicant) return null

  return (
    <CellFrame sx={{ px: 5, pb: 8 }}>
      <LotteryApplicationItem title={t('ticketInfo', '抽選情報')}>
        <Typography variant="h5" sx={{ pt: 3, pb: 2 }}>
          {preSaleLotteryApplicant.preSale.title}
        </Typography>
        <Typography sx={{ pt: 3 }} variant="subtitle1" color="text.secondary">
          {t('applicationDate')}
        </Typography>
        <Typography sx={{ pb: 3 }} variant="body2">
          {DateTime.format(
            preSaleLotteryApplicant.preSale.lottery?.applicationStartedAt
          )}{' '}
          〜{' '}
          {DateTime.format(
            preSaleLotteryApplicant.preSale.lottery?.applicationEndedAt
          )}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {t('resultNoticeDate')}
        </Typography>
        <Typography sx={{ pb: 3 }} variant="body2">
          {DateTime.format(preSaleLotteryApplicant.resultNoticedAt)}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {t('purchasableDate')}
        </Typography>
        <Typography sx={{ pb: 3 }} variant="body2">
          {DateTime.format(
            preSaleLotteryApplicant.preSale.lottery?.purchasableStartedAt
          )}{' '}
          〜{' '}
          {DateTime.format(
            preSaleLotteryApplicant.preSale.lottery?.purchasableEndedAt
          )}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {t('applicatedAt')}
        </Typography>
        <Typography sx={{ pb: 3 }} variant="body2">
          {DateTime.format(preSaleLotteryApplicant.appliedAt)}
        </Typography>
      </LotteryApplicationItem>
      <LotteryApplicationItem title={t('applicationInfo', '応募情報')}>
        <Stack spacing={6}>
          {preSaleLotteryApplicant?.choiceItemDetails.map(
            (choiceItemDetail, index) => (
              <Box key={index}>
                <LotteryChoiceItem
                  index={index}
                  choiceLength={
                    preSaleLotteryApplicant?.choiceItemDetails?.length
                  }
                  eventTitle={eventTitle}
                  choiceItemDetail={choiceItemDetail}
                  winChoiceNumber={preSaleLotteryApplicant?.winChoiceNumber}
                  applicantStatus={preSaleLotteryApplicant?.status}
                />
              </Box>
            )
          )}
        </Stack>
      </LotteryApplicationItem>

      {/* クレカ決済かつ当選・抽選待ちのとき、支払い情報を表示 */}
      {[
        '10_win',
        '11_win_paid',
        '20_waiting_payment',
        '30_waiting_lottery',
      ].includes(preSaleLotteryApplicant.status) &&
        !isKonbini && (
          <LotteryApplicationItem title={t('paymentInfo', '支払い情報')}>
            <Stack direction="row" alignItems="center" spacing={3}>
              <Stack sx={{ py: 3 }}>
                <Stack direction="row" spacing={3} alignItems="center">
                  <StorageImage
                    height={25}
                    src={generateCreditCardLogoPath(paymentSource?.brand)}
                    target="common"
                  />
                  <Typography variant="h6">
                    {t('last4Digits', { number: paymentSource?.last4 })}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </LotteryApplicationItem>
        )}

      {/* 当選または抽選待ちのとき、支払い総額を表示 */}
      {[
        '10_win',
        '11_win_paid',
        '20_waiting_payment',
        '30_waiting_lottery',
      ].includes(preSaleLotteryApplicant.status) && (
        <LotteryApplicationItem title={t('totalPriceHead', 'お支払い総額')}>
          {preSaleLotteryApplicant?.choiceItemDetails.map(
            (choiceItemDetail, index) => (
              <LotteryChoicePrice
                key={index}
                index={index}
                choiceLength={
                  preSaleLotteryApplicant?.choiceItemDetails?.length
                }
                choiceItemDetail={choiceItemDetail}
                winChoiceNumber={preSaleLotteryApplicant?.winChoiceNumber}
              />
            )
          )}
        </LotteryApplicationItem>
      )}

      {isShowUsageTicketNote && (
        <Stack>
          <Typography variant="caption" color="text.secondary">
            {t('usageTicketNote')}
          </Typography>
        </Stack>
      )}

      {/* 下部ボタン、当選前はキャンセル導線。支払い前はクレカのみ導線表示、支払い後はアイテム詳細ページへ */}
      {isShowButton && (
        <Stack sx={{ pt: 8 }}>
          <Stack direction="row" sx={{ justifyContent: 'center' }}>
            <Button
              onClick={() => {
                switch (preSaleLotteryApplicant.status) {
                  case '30_waiting_lottery':
                    if (isButtonDisabled) return
                    onCancel()
                    break
                  case '20_waiting_payment':
                    router.push(
                      generatePath(
                        routerPages.LotteryApplicationDetailPaymentConfirmation,
                        {
                          preSaleId: preSaleId ?? '#',
                        }
                      )
                    )
                    break
                  default:
                    ;(() => {
                      if (!winChoicedItem) return
                      const { itemId, skuId } = winChoicedItem

                      router.push({
                        pathname: generatePath(routerPages.OwnedItemDetail, {
                          itemId,
                        }),
                        search: skuId
                          ? `?skuId=${encodeSkuId(skuId ?? '')}`
                          : '',
                      })
                    })()
                }
              }}
              variant={
                preSaleLotteryApplicant.status === '30_waiting_lottery'
                  ? 'outlined'
                  : 'contained'
              }
              size="large"
              sx={{
                py: 2,
                px: 4,
                width: 244,
              }}
              disabled={isButtonDisabled}
            >
              {t(`${preSaleLotteryApplicant.status}_button`)}
            </Button>
          </Stack>
        </Stack>
      )}
    </CellFrame>
  )
}

const LotteryApplicationItem: Cell<{ title: string }> = ({
  children,
  title,
}) => (
  <CellFrame>
    <Stack spacing={1} sx={{ pb: 4 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ bgcolor: 'grey.50', py: 1, px: 2 }}
      >
        <Typography variant="h6" color="text.secondary">
          {title}
        </Typography>
      </Stack>
      {children}
    </Stack>
  </CellFrame>
)

type FixedBottomCellProps = {
  expiredAt?: string | null
  voucherUrl?: string | null
}
const FixedBottomCell: Cell<FixedBottomCellProps> = ({
  expiredAt,
  voucherUrl,
}) => {
  const { t } = useTranslation('lotteryApplicationDetail')
  const { onClick } = useBrowserLink(voucherUrl ?? '')

  if (!expiredAt || !voucherUrl) {
    return null
  }

  return (
    <CellFrame sx={{ bgcolor: 'grey.50' }}>
      <Stack sx={{ p: 5 }} alignItems="center" spacing={3}>
        <Typography variant="subtitle1">
          {t('paymentDeadlineKonbini', { expiredAt })}
        </Typography>
        <Button
          variant="text"
          color="primary"
          sx={{ width: 160 }}
          disabled={!voucherUrl}
          onClick={onClick}
        >
          {t('confirmPayment')}
        </Button>
      </Stack>
    </CellFrame>
  )
}
